// Creating a tie: find a date, lay the whole tie out on it, save once.
// See kumpula/TEAMS.md.
//
// The flow follows how a reservation is actually made in this app: browse availability until you
// find a day with room, then place every rubber on it, then commit. Three consequences worth
// knowing before changing anything here:
//
//  1. NOTHING IS BOOKED WHILE THE MANAGER IS CHOOSING. Placements live in local state until the
//     save, so moving one costs nothing and "propose the rest" is a suggestion rather than a
//     booking to undo. The flip side is that the server still reports those courts as free, so
//     pending picks have to be filtered out of the grid client-side.
//
//  2. THE TIE'S DATE IS THE DAY THAT WAS BROWSED. Every rubber comes from that day's grid, so a
//     tie's date and its rubbers' dates cannot drift apart - which matters because the
//     opponent's page prints both, and nothing can edit a tie's date afterwards.
//
//  3. SAVING IS createtie FOLLOWED BY ONE bookrubber PER PLACEMENT. The backend has no atomic
//     "create with rubbers", and quota is claimed by createtie, so a partial failure leaves a
//     real tie holding fewer rubbers than planned. That is a valid tie - the tally is a readout,
//     not a gate - so the failures are reported and the successes kept, never rolled back.
//
// Editing is deliberately absent: no callable can change a tie's date, note or rubbers, so the
// way to change one is to cancel it (releasing the quota) and build a new one.
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Container, Row, Col, Card, CardBody, CardHeader, Button, Alert, Spinner, Badge } from 'reactstrap'
import { List, TextField } from '@material-ui/core'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import fi from 'date-fns/locale/fi'
import sv from 'date-fns/locale/sv'
import et from 'date-fns/locale/et'
import enGB from 'date-fns/locale/en-GB'
import moment from 'moment'
import withAppconfig from '../../AppContext'
import TopNavigation from '../../TopNavigation'
import LottieLoading from '../../components/LottieLoading'
import SimpleView from '../../components/SimpleView'
import { useManagerTeams } from '../../utils/useManagerTeams'
import { useCourts } from '../../utils/useCourts'
import { useTeamsApi } from '../../utils/teamsapi'
import { describeFormat, formatMinutes, rubberTypeLabel } from '../../utils/teamformats'
import {
  teamContext,
  seasonWindow,
  formatDateRange,
  momentLocale,
  seasonDayFilter,
  availabilitySlots,
  tiesOnDate,
  remainingRubbers,
  planDay,
  rubberEnd,
  formatRubberTime,
} from '../../utils/teamsview'
import { TeamsFailure, ChoiceGuidance } from './TeamsScreen'

// Registered under the same names momentLocale() returns, so one language mapping serves both
// the picker and the times rendered beside it.
registerLocale('fi', fi)
registerLocale('sv', sv)
registerLocale('et', et)
registerLocale('en-gb', enGB)

const toDate = (iso) => (iso ? moment.utc(iso, 'YYYY-MM-DD').toDate() : null)

// One line per placement the manager has chosen, each removable while nothing is saved yet.
function Picks({ picks, onRemove, disabled }) {
  const { t, i18n } = useTranslation()
  if (!picks.length) return <div className='text-muted'>{t('teams_no_picks_yet')}</div>
  return (
    <div>
      {picks.map((pick, i) => (
        <div key={i} className='d-flex justify-content-between align-items-center py-1'>
          <div>
            <strong>{rubberTypeLabel(pick.type)}</strong>{' '}
            {formatRubberTime(pick.start, pick.end, i18n.language)}
            {pick.courtname ? ' - ' + pick.courtname : ''}
          </div>
          <Button
            size='sm'
            color='link'
            className='text-danger'
            disabled={disabled}
            onClick={() => onRemove(i)}
          >
            {t('teams_remove')}
          </Button>
        </div>
      ))}
    </div>
  )
}

function TeamTieScreen({ appconfig }) {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { teamid } = useParams()
  const { search } = useLocation()
  const api = useTeamsApi({ appconfig })
  const { loading, data, failure, reload } = useManagerTeams({ appconfig })

  const [date, setDate] = useState('')
  const [availability, setAvailability] = useState(null)
  const [loadingGrid, setLoadingGrid] = useState(false)
  const [picks, setPicks] = useState([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [report, setReport] = useState(null)
  const [actionFailure, setActionFailure] = useState(null)

  const { team, season, format, ties, quota } = teamContext(data, teamid)
  const window = seasonWindow(season, moment().format('YYYY-MM-DD'))
  const courts = useCourts({ appconfig })
  const step = (appconfig.times && appconfig.times.i) || 30


  // Start on the day the season overview sent us to, else the first day the season allows -
  // never an empty picker. The ?date= is validated rather than trusted: it comes from a URL.
  useEffect(() => {
    if (date || !season) return
    const wanted = new URLSearchParams(search).get('date')
    if (wanted && moment.utc(wanted, 'YYYY-MM-DD', true).isValid()) {
      setDate(wanted)
      return
    }
    const today = moment().format('YYYY-MM-DD')
    setDate(season.validstart && today < season.validstart ? season.validstart : today)
  }, [season, date, search])

  const fetchAvailability = useCallback(
    (forDate) => {
      if (!forDate) return Promise.resolve(null)
      setLoadingGrid(true)
      return api.availability({ teamid, date: forDate }).then((res) => {
        setLoadingGrid(false)
        if (!res.ok) {
          setActionFailure(res)
          setAvailability(null)
          return null
        }
        setActionFailure(null)
        setAvailability(res.data)
        return res.data
      })
    },
    // `api` is rebuilt every render; the team is what should invalidate this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamid]
  )

  // Changing the day invalidates every placement - they were times on the old day.
  useEffect(() => {
    if (!date) return
    setPicks([])
    setActiveIndex(0)
    setReport(null)
    fetchAvailability(date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date])

  const sameDay = tiesOnDate(ties, date)
  const remaining = remainingRubbers(format, picks)
  const plan = planDay(availability, format, { courts, step, picks })
  // Exactly one rubber is being placed at a time. Tracked by position rather than by row,
  // because two pills for the same format row are indistinguishable and comparing by row would
  // highlight every copy at once.
  // The first remaining rubber is selected by default, and placing one moves the selection to
  // the next: the manager works straight down the list without reselecting each time. Tracked by
  // position rather than by row, because two pills for the same format row are indistinguishable
  // and comparing by row would highlight every copy at once.
  const activeIdx = activeIndex >= 0 && activeIndex < remaining.length ? activeIndex : 0
  const row = remaining[activeIdx] || null
  const slots = row ? availabilitySlots(availability, row, { courts, step, picks }) : []

  const addPick = (slot) => {
    if (!row) return
    setPicks(
      picks.concat({
        rubberid: row.rubberid,
        type: row.type,
        duration: row.duration,
        resourceid: slot.id,
        courtname: slot.courtname || slot.displayName || '',
        start: slot.start,
        end: rubberEnd(slot.start, row.duration),
      })
    )
    setActiveIndex(0)
  }

  const removePick = (index) => setPicks(picks.filter((_, i) => i !== index))

  const proposeRest = () => setPicks(picks.concat(plan.placed))

  // One bookrubber per placement, in order. A court someone else took must not stop the rest, so
  // failures are collected rather than aborting.
  const bookAll = async (tieid, list) => {
    const done = []
    const failed = []
    for (const pick of list) {
      // eslint-disable-next-line no-await-in-loop
      const res = await api.bookRubber({
        tieid,
        rubberid: pick.rubberid,
        resourceid: pick.resourceid,
        start: pick.start,
      })
      if (res.ok) done.push(pick)
      else failed.push({ pick, message: res.message, code: res.code })
    }
    return { done, failed }
  }

  const save = async () => {
    setActionFailure(null)
    setSaving(true)

    const created = await api.createTie({ teamid, date, opponent: note })
    if (!created.ok) {
      setSaving(false)
      setActionFailure(created)
      return
    }

    const tieid = created.data.tieid
    const { done, failed } = await bookAll(tieid, picks)
    setSaving(false)
    setPicks(failed.map((f) => f.pick))
    setReport({ tieid, placed: done.length, failed })
    reload()
    if (failed.length) fetchAvailability(date)
  }

  // Retry only what failed, into the tie that already exists.
  const retryRest = async () => {
    if (!report) return
    setSaving(true)
    const { done, failed } = await bookAll(report.tieid, picks)
    setSaving(false)
    setPicks(failed.map((f) => f.pick))
    setReport({ tieid: report.tieid, placed: report.placed + done.length, failed })
    reload()
    if (failed.length) fetchAvailability(date)
  }

  const cancelTie = () => {
    if (!report) return
    setSaving(true)
    api.cancelTie({ tieid: report.tieid }).then((res) => {
      setSaving(false)
      if (!res.ok) {
        setActionFailure(res)
        return
      }
      reload()
      history.push(appconfig.pathPrefix + '/teams/' + teamid + '/season')
    })
  }

  const goSeason = () => history.push(appconfig.pathPrefix + '/teams/' + teamid + '/season')

  const startOver = () => {
    setReport(null)
    setPicks([])
    setNote('')
    setActiveIndex(0)
    fetchAvailability(date)
  }

  if (loading) {
    return (
      <div className='full-page-background'>
        <TopNavigation />
        <Container>
          <LottieLoading width={150} height={150} />
        </Container>
      </div>
    )
  }

  if (!team) {
    return (
      <div className='full-page-background'>
        <TopNavigation />
        <Container>
          <TeamsFailure failure={failure} />
          {!failure ? (
            <Alert color='secondary' className='mt-3'>{t('teams_not_found')}</Alert>
          ) : null}
        </Container>
      </div>
    )
  }

  // --- after a save: what landed, what did not, and what to do about it ----
  if (report) {
    return (
      <div className='full-page-background'>
        <TopNavigation />
        <Container>
          <Row className='justify-content-center'>
            <Col xs='12' md='8'>
              <Card className='mt-3'>
                <CardHeader>
                  {team.name} - {date}
                </CardHeader>
                <CardBody>
                  <TeamsFailure failure={actionFailure} />

                  {report.failed.length === 0 ? (
                    <Alert color='success'>
                      {t('teams_save_ok').replace('{PLACED}', report.placed)}
                    </Alert>
                  ) : (
                    <>
                      <Alert color='warning'>
                        {t('teams_save_partial')
                          .replace('{PLACED}', report.placed)
                          .replace('{FAILED}', report.failed.length)}
                      </Alert>
                      {report.failed.map((f, i) => (
                        <div key={i} className='small text-danger'>
                          {rubberTypeLabel(f.pick.type)}{' '}
                          {formatRubberTime(f.pick.start, f.pick.end, i18n.language)}
                          {f.pick.courtname ? ' - ' + f.pick.courtname : ''}
                          {f.message ? ': ' + f.message : ''}
                        </div>
                      ))}

                      <div className='mt-3'>
                        <div className='small text-muted mb-1'>{t('teams_retry_help')}</div>
                        {/* The failed placements are back in the picker, so they can be dropped
                            before retrying. */}
                        <Picks picks={picks} onRemove={removePick} disabled={saving} />
                        <div className='mt-2'>
                          <Button
                            color='primary'
                            disabled={saving || !picks.length}
                            onClick={retryRest}
                          >
                            {saving ? <Spinner size='sm' /> : t('teams_retry_rest')}
                          </Button>
                          <Button
                            color='danger'
                            outline
                            className='ml-2'
                            disabled={saving}
                            onClick={cancelTie}
                          >
                            {t('teams_cancel_tie')}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}

                  <div className='mt-3'>
                    <Button color='secondary' onClick={goSeason}>
                      {t('teams_go_season')}
                    </Button>
                    <Button color='link' disabled={saving} onClick={startOver}>
                      {t('teams_create_another')}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  return (
    <div className='full-page-background'>
      <TopNavigation />
      <Container>
        <Row>
          <Col xs='12'>
            <h4 className='mt-3'>{team.name}</h4>
            <div className='text-muted'>{describeFormat(format)}</div>
            <Button color='link' className='pl-0' onClick={goSeason}>
              {t('teams_back_to_season')}
            </Button>
          </Col>
        </Row>

        <Row>
          <Col xs='12' md='7'>
            <TeamsFailure failure={actionFailure} />
            <ChoiceGuidance team={team} quota={quota} />

            {window.state === 'not-open' ? (
              <Alert color='secondary' className='mt-2'>
                {t('teams_booking_not_open').replace('{DATE}', window.opens || '')}
              </Alert>
            ) : null}
            {window.state === 'closed' ? (
              <Alert color='secondary' className='mt-2'>{t('teams_booking_closed')}</Alert>
            ) : null}

            {/* Step 1 - find a day with room for the whole tie. */}
            <Card className='mt-3 mb-3'>
              <CardHeader>{t('teams_step_date')}</CardHeader>
              <CardBody>
                {/* The date may only be chosen from the calendar: onChangeRaw prevents a typed
                    one from ever being applied. A typed date is the usual source of "this does
                    not work" - it can miss the season window, land on a day the league does not
                    play, or just be a typo, and a tie's date is fixed once created.
                    readOnly is NOT the way to do this: the library skips opening the calendar
                    when it is set (`readOnly||setOpen(true)` in react-datepicker's dist), which
                    would leave no way to pick a date at all. Preventing the raw change blocks
                    typing while the calendar, including its arrow-key navigation, still works. */}
                <DatePicker
                  selected={toDate(date)}
                  onChange={(d) => setDate(d ? moment(d).format('YYYY-MM-DD') : '')}
                  minDate={toDate(season && season.validstart)}
                  maxDate={toDate(season && season.validend)}
                  filterDate={seasonDayFilter(season) || undefined}
                  dateFormat='EEEEEE d.M.yyyy'
                  locale={momentLocale(i18n.language)}
                  onChangeRaw={(e) => e.preventDefault()}
                />
                {season && formatDateRange(season.validstart, season.validend) ? (
                  <div className='small text-muted mt-1'>
                    {t('teams_season_active')}{' '}
                    {formatDateRange(season.validstart, season.validend)}
                  </div>
                ) : null}

                {/* Two ties on one day is legal - a club can host two encounters in an evening -
                    so this warns rather than blocks. It is far more often a duplicate, which
                    costs quota and can only be undone by cancelling. */}
                {sameDay.length ? (
                  <Alert color='warning' className='mt-2 mb-0 py-2 small'>
                    {t('teams_same_day').replace('{COUNT}', sameDay.length)}
                  </Alert>
                ) : null}

                {/* How much of the tie this day can hold, counting what is already placed. It is
                    a heuristic: a day it calls short may still work in another arrangement, so
                    the grid below stays available either way. */}
                {loadingGrid ? (
                  <Spinner size='sm' color='primary' className='mt-2' />
                ) : availability ? (
                  <Alert
                    color={plan.fits >= plan.expected ? 'success' : 'secondary'}
                    className='mt-2 mb-0 py-2'
                  >
                    {plan.fits >= plan.expected
                      ? t('teams_fits_all').replace('{EXPECTED}', plan.expected)
                      : t('teams_fits')
                          .replace('{FITS}', plan.fits)
                          .replace('{EXPECTED}', plan.expected)}
                  </Alert>
                ) : null}
              </CardBody>
            </Card>

            {/* Step 2 - place each rubber. The manager picks a row, then a time and court; the
                length is the format row's and never an input. */}
            {window.canBook && team.state === 'active' ? (
              <Card className='mb-3'>
                <CardHeader>{t('teams_step_layout')}</CardHeader>
                <CardBody>
                  {remaining.length === 0 ? (
                    <Alert color='success' className='mb-0'>{t('teams_tie_complete')}</Alert>
                  ) : (
                    <>
                      <div className='small text-muted mb-1'>{t('teams_pick_rubber')}</div>
                      <div className='mb-2'>
                        {remaining.map((r, i) => (
                          <Button
                            key={r.rubberid + '-' + i}
                            color={i === activeIdx ? 'primary' : 'secondary'}
                            className='rounded-pill mr-2 mb-2'
                            onClick={() => setActiveIndex(i)}
                          >
                            {rubberTypeLabel(r.type)} {formatMinutes(r.duration)}
                          </Button>
                        ))}
                      </div>

                      {/* What the suggestion will do, in words. The order is not cosmetic: the
                          people who play the doubles have just played the singles, so a plan
                          that ran them at once would be unplayable. */}
                      {plan.placed.length ? (
                        <div className='mb-2'>
                          <Button color='secondary' outline size='sm' onClick={proposeRest}>
                            {t('teams_propose_rest').replace('{COUNT}', plan.placed.length)}
                          </Button>
                          {plan.rule === 'after-singles' ? (
                            <div className='small text-muted mt-1'>{t('teams_rule_after_singles')}</div>
                          ) : null}
                          {plan.rule === 'with-last-single' ? (
                            <div className='small text-warning mt-1'>{t('teams_rule_with_last')}</div>
                          ) : null}
                        </div>
                      ) : null}

                      {plan.rule === 'doubles-do-not-fit' ? (
                        <Alert color='warning' className='py-2 small'>
                          {t('teams_rule_no_double')}
                        </Alert>
                      ) : null}

                      {row ? (
                        <div>
                          <div className='small text-muted'>
                            {t('teams_placing').replace(
                              '{RUBBER}',
                              rubberTypeLabel(row.type) + ' ' + formatMinutes(row.duration)
                            )}
                          </div>
                          {loadingGrid ? (
                            <Spinner color='primary' />
                          ) : slots.length === 0 ? (
                            <Alert color='secondary' className='mb-0 mt-2'>
                              {t('teams_no_availability')}
                            </Alert>
                          ) : (
                            // The ordinary grid, fed slots at exactly this row's length.
                            // `recurrentsales` suppresses the price badge - availability
                            // carries no price.
                            <List component='nav'>
                              <SimpleView
                                dailyFreeTimes={slots}
                                selectedvenue={'all'}
                                weather={null}
                                viewEmptyTimes={false}
                                showWeek={false}
                                recurrentsales={true}
                                hideBanner={true}
                                onSelectSlot={addPick}
                                appconfig={appconfig}
                              />
                            </List>
                          )}
                        </div>
                      ) : null}
                    </>
                  )}
                </CardBody>
              </Card>
            ) : null}
          </Col>

          {/* Step 3 - the note, and one save. */}
          <Col xs='12' md='5'>
            <Card className='mt-3 mt-md-0'>
              <CardHeader>
                {t('teams_step_save')}
                <Badge color='light' className='ml-2'>
                  {picks.length}/{plan.expected}
                </Badge>
              </CardHeader>
              <CardBody>
                <Picks picks={picks} onRemove={removePick} disabled={saving} />

                <TextField
                  className='mt-3'
                  fullWidth
                  label={t('teams_tie_note')}
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 200))}
                  helperText={t('teams_tie_note_help')}
                />

                {/* Nothing can edit a tie afterwards, so say so before they commit. */}
                <Alert color='secondary' className='mt-3 small'>
                  {t('teams_save_is_final')}
                </Alert>

                <Button
                  color='primary'
                  disabled={saving || !picks.length || !date || !window.canBook}
                  onClick={save}
                >
                  {saving ? <Spinner size='sm' /> : t('teams_save_tie')}
                </Button>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default withAppconfig(TeamTieScreen)
